import React, { useState } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Drawer, List, ListItem,
  ListItemText, Box, Container, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import logo from '../assets/logo-mark.png';
import { company, navLinks } from '../data/company';
import { brand, serif } from '../theme';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: brand.paper,
    color: brand.ink,
    borderBottom: '1px solid transparent',
    transition: 'border-color 200ms ease, background-color 200ms ease',
  },
  appBarScrolled: {
    backgroundColor: 'rgba(245,242,236,0.96)',
    borderBottomColor: brand.line,
    backdropFilter: 'saturate(160%) blur(8px)',
  },
  toolbar: {
    minHeight: 64,
    padding: 0,
    [theme.breakpoints.up('md')]: {
      minHeight: 80,
    },
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    color: 'inherit',
    textDecoration: 'none',
    marginRight: 'auto',
    minWidth: 0,
  },
  logo: {
    width: 34,
    height: 34,
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  wordmark: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1,
  },
  brandName: {
    fontFamily: serif,
    fontSize: '1.35rem',
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
  },
  brandTag: {
    fontSize: '0.62rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: brand.muted,
    marginTop: 4,
  },
  links: {
    display: 'none',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  link: {
    color: 'inherit',
    fontWeight: 500,
    textDecoration: 'none',
    padding: '6px 0',
    margin: '0 16px',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 1,
      backgroundColor: brand.accent,
      transform: 'scaleX(0)',
      transformOrigin: 'left',
      transition: 'transform 200ms ease',
    },
    '&:hover::after, &:focus-visible::after': {
      transform: 'scaleX(1)',
    },
  },
  phone: {
    marginLeft: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    borderLeft: `1px solid ${brand.line}`,
    display: 'flex',
    alignItems: 'center',
    color: 'inherit',
    textDecoration: 'none',
    fontWeight: 600,
    '& svg': {
      fontSize: 18,
      marginLeft: 8,
      color: brand.accent,
      transition: 'transform 200ms ease',
    },
    '&:hover svg': {
      transform: 'translateX(3px)',
    },
  },
  menuButton: {
    fontWeight: 600,
    letterSpacing: '0.12em',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    padding: '8px 0 8px 12px',
    minWidth: 0,
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  drawerPaper: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: brand.paper,
    padding: theme.spacing(2, 3, 4),
    // Leave room under the call button for Netlify's "Powered by Netlify"
    // badge, which is pinned to the bottom-right corner on the free plan.
    paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  drawerLink: {
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${brand.line}`,
    '& .MuiListItemText-primary': {
      fontFamily: serif,
      fontSize: '1.75rem',
      lineHeight: 1.2,
    },
  },
  drawerNumber: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: brand.accent,
    width: 36,
    flexShrink: 0,
  },
  drawerFooter: {
    marginTop: 'auto',
    paddingTop: theme.spacing(4),
  },
}));

export default function NavBar() {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        className={`${classes.appBar} ${scrolled ? classes.appBarScrolled : ''}`}>
        <Container maxWidth="lg">
          <Toolbar className={classes.toolbar}>
            <a href="#top" className={classes.brand} aria-label={`${company.name} home`}>
              <img src={logo} alt="" className={classes.logo} />
              <span className={classes.wordmark}>
                <span className={classes.brandName}>{company.shortName}</span>
                <span className={classes.brandTag}>General contractor</span>
              </span>
            </a>

            <nav className={classes.links} aria-label="Main">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className={classes.link}>
                  {link.label}
                </a>
              ))}
              <a href={company.phoneHref} className={classes.phone}>
                {company.phoneDisplay}
                <ArrowForwardIcon />
              </a>
            </nav>

            <Button
              color="inherit"
              className={classes.menuButton}
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu">
              Menu
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        classes={{ paper: classes.drawerPaper }}>
        <Box className={classes.drawerHeader}>
          <Typography variant="overline" component="span" color="textSecondary">Menu</Typography>
          <IconButton onClick={closeDrawer} edge="end" aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <List component="nav" aria-label="Main" disablePadding>
          {navLinks.map((link, index) => (
            <ListItem
              key={link.href}
              button
              component="a"
              href={link.href}
              onClick={closeDrawer}
              className={classes.drawerLink}>
              <span className={classes.drawerNumber}>{String(index + 1).padStart(2, '0')}</span>
              <ListItemText primary={link.label} />
            </ListItem>
          ))}
        </List>
        <Box className={classes.drawerFooter}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Call for a free estimate
          </Typography>
          <Button
            fullWidth
            size="large"
            href={company.phoneHref}
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}>
            {company.phoneDisplay}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
